import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';

function ContactPage() {
  const { isLoggedIn, user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pre-fill name and email for logged-in users
  useEffect(() => {
    if (isLoggedIn && user) {
      if (!name) {
        setName(user.name || '');
      }
      if (!email) {
        setEmail(user.email || '');
      }
    }
  }, [user, isLoggedIn, name, email]);

  const validateForm = () => {
    if (!name.trim()) {
      setError('Name is required');
      return false;
    }
    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters long');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!message.trim()) {
      setError('Message is required');
      return false;
    }
    if (message.trim().length < 10) {
      setError('Message must be at least 10 characters long');
      return false;
    }
    if (message.length > 5000) {
      setError('Message must not exceed 5000 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await apiService.post('/api/contact', {
        name: name.trim(),
        email: email.trim(),
        message: message.trim()
      });

      setSuccess(true);
      // Clear form
      setName('');
      setEmail('');
      setMessage('');

      // Scroll to top to see success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-200 flex flex-col">
      <SiteHeader />

      <main className="flex-1 py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl font-extrabold text-primary-700 mb-4 text-center">
            Contact Us
          </h1>
          <p className="text-center text-primary-800 mb-8">
            Have a question or feedback? We'd love to hear from you!
          </p>

          <div className="bg-white rounded-lg shadow-md p-8">
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                <p className="text-green-700 font-medium">
                  Thank you for your message! We'll get back to you soon.
                </p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="name" className="block mb-2 font-medium text-primary-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-primary-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Your name"
                  maxLength={100}
                  disabled={loading}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block mb-2 font-medium text-primary-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-primary-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                  maxLength={255}
                  disabled={loading}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block mb-2 font-medium text-primary-700">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="w-full border border-primary-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  placeholder="Tell us what's on your mind..."
                  maxLength={5000}
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {message.length} / 5000 characters
                </p>
              </div>

              <div className="flex gap-4 items-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
                <Link
                  to={isLoggedIn ? "/dashboard" : "/"}
                  className="text-primary-500 hover:text-primary-600 font-medium hover:underline"
                >
                  Back to Home
                </Link>
              </div>
            </form>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-gray-500 text-sm bg-white border-t border-primary-300 mt-auto">
        <div className="flex justify-center gap-6 mb-2">
          <Link to="/" className="hover:text-primary-500">Home</Link>
          <Link to="/faq" className="hover:text-primary-500">FAQ</Link>
          <Link to="/contact" className="hover:text-primary-500">Contact</Link>
        </div>
        <span>&copy; {new Date().getFullYear()} My Video Gallery</span>
      </footer>
    </div>
  );
}

export default ContactPage;
